import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { TEST_IDS } from "@/constants/testIds";
import StatusBadge from "@/components/StatusBadge";
import { BarChart, Bar, XAxis, ResponsiveContainer, Cell, PieChart, Pie } from "recharts";
import {
  CalendarDays, ListTodo, AlertOctagon, CheckCircle2, FileAudio, Gavel,
  ArrowUpRight, Users, Sparkles, ClipboardCheck, Gauge, Activity, AlertTriangle,
} from "lucide-react";

function Kpi({ label, value, icon: Icon, tone = "lavender", testid }) {
  return (
    <div
      data-testid={testid}
      className="yl-surface yl-hover-lift yl-beam p-5 min-h-[128px] flex flex-col justify-between"
    >
      <div className="flex items-start justify-between gap-2">
        <div className="eyebrow leading-tight">{label}</div>
        <div className={`tone-${tone} w-9 h-9 rounded-xl grid place-items-center shrink-0`}>
          <Icon className="w-4 h-4" strokeWidth={1.8} />
        </div>
      </div>
      <div className="mt-4 font-heading text-4xl leading-none">{value}</div>
    </div>
  );
}

// Header copy + CTA per role.
const ROLE_HEADER = {
  Admin: { eyebrow: "Admin · workspace overview", title: "Workspace." },
  Organizer: { eyebrow: "Organizer overview", title: "Dashboard." },
  Reviewer: { eyebrow: "Reviewer · decisions & progress", title: "Review." },
  Participant: { eyebrow: "My meetings & tasks", title: "Today." },
};

export default function Dashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get("/dashboard/organizer").then(({ data }) => setData(data)).catch(() => {}),
      api.get("/ac/stats").then(({ data }) => setStats(data)).catch(() => {}),
    ]).finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="p-6 md:p-8 lg:p-12 space-y-8" data-testid="dashboard-loading">
        <div className="h-10 w-56 rounded-xl bg-secondary animate-pulse" />
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[0, 1, 2, 3].map((i) => <div key={i} className="h-32 rounded-3xl bg-secondary animate-pulse" />)}
        </div>
        <div className="h-72 rounded-3xl bg-secondary animate-pulse" />
      </div>
    );
  }
  const k = data?.kpis || {};
  const role = data?.role || user?.role || "Organizer";
  const headerCopy = ROLE_HEADER[role] || ROLE_HEADER.Organizer;
  const canCreateMeeting = role === "Admin" || role === "Organizer";

  // KPI tile set varies by role for clarity.
  const showAdminTiles = role === "Admin";
  const showOrganizerTiles = role === "Admin" || role === "Organizer";
  const showReviewerTiles = role === "Admin" || role === "Reviewer";
  const showParticipantTiles = role === "Participant";

  return (
    <div data-testid={TEST_IDS.dashboardRoot} className="p-6 md:p-8 lg:p-12 space-y-10 max-w-[1600px]">
      <header className="flex items-end justify-between flex-wrap gap-4 yl-fade-up">
        <div>
          <div className="eyebrow flex items-center gap-2.5">
            {headerCopy.eyebrow}
            <span
              data-testid="dashboard-role-pill"
              className="rounded-full bg-secondary text-secondary-foreground px-2.5 py-0.5 text-[9px] tracking-[0.18em] font-bold"
            >
              {role}
            </span>
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl mt-3 leading-none">
            {headerCopy.title}
          </h1>
        </div>
        {canCreateMeeting && (
          <Link
            data-testid="dashboard-new-meeting-link"
            to="/meetings"
            className="group inline-flex items-center gap-2 h-11 px-5 rounded-full bg-primary text-primary-foreground text-sm font-bold shadow-lift hover:bg-[hsl(var(--primary-hover))] transition-colors"
          >
            New meeting
            <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" strokeWidth={2.2} />
          </Link>
        )}
      </header>

      {/* KPIs — composed per role */}
      <section className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4">
        {showAdminTiles && (
          <>
            <Kpi label="Users" value={k.user_count || 0} icon={Users} tone="lavender" testid="kpi-user-count" />
            <Kpi label="Organizers" value={k.organizer_count || 0} icon={Users} tone="powder" testid="kpi-organizer-count" />
          </>
        )}
        {(showOrganizerTiles || showReviewerTiles) && (
          <Kpi label="Total Meetings" value={k.total_meetings || 0} icon={CalendarDays} tone="lavender" testid={TEST_IDS.kpiTotalMeetings} />
        )}
        {showOrganizerTiles && (
          <Kpi label="Open Tasks" value={k.open_tasks || 0} icon={ListTodo} tone="powder" testid={TEST_IDS.kpiOpenTasks} />
        )}
        {(showOrganizerTiles || showReviewerTiles) && (
          <Kpi label="Overdue Tasks" value={k.overdue_tasks || 0} icon={AlertOctagon} tone="blush" testid={TEST_IDS.kpiOverdueTasks} />
        )}
        {showOrganizerTiles && (
          <Kpi label="Completed Tasks" value={k.completed_tasks || 0} icon={CheckCircle2} tone="sage" testid={TEST_IDS.kpiCompletedTasks} />
        )}
        {showOrganizerTiles && (
          <Kpi label="Pending Transcription" value={k.pending_transcription || 0} icon={FileAudio} tone="apricot" testid={TEST_IDS.kpiPendingTranscription} />
        )}
        {(showOrganizerTiles || showReviewerTiles) && (
          <Kpi label="Decisions Taken" value={k.decisions_taken || 0} icon={Gavel} tone="lavender" testid={TEST_IDS.kpiDecisionsTaken} />
        )}
        {showParticipantTiles && (
          <>
            <Kpi label="My Open Tasks" value={(data?.my_tasks || []).filter((t) => t.status === "Open").length} icon={ListTodo} tone="powder" testid="kpi-my-open-tasks" />
            <Kpi label="My Overdue" value={k.overdue_tasks || 0} icon={AlertOctagon} tone="blush" testid="kpi-my-overdue" />
            <Kpi label="My Meetings" value={k.total_meetings || 0} icon={CalendarDays} tone="lavender" testid="kpi-my-meetings" />
            <Kpi label="My Tasks Done" value={(data?.my_tasks || []).filter((t) => t.status === "Completed").length} icon={CheckCircle2} tone="sage" testid="kpi-my-done" />
          </>
        )}
      </section>

      {/* Execution analytics — folded in from the former Action Center */}
      {stats && (
        <section data-testid="dashboard-analytics" className="space-y-6">
          <div className="flex items-center gap-2">
            <Activity className="w-4 h-4 text-primary" strokeWidth={2} />
            <h2 className="font-heading text-xl sm:text-2xl">Execution analytics</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
            {/* Meeting health hero */}
            <div data-testid="analytics-meeting-health" className="yl-surface yl-beam p-6 flex flex-col justify-between">
              <div className="flex items-center justify-between">
                <div className="eyebrow">Meeting health</div>
                <Gauge className="w-5 h-5 text-primary" strokeWidth={1.8} />
              </div>
              <div className="mt-4">
                <div className="font-heading text-5xl leading-none">
                  {stats.avg_meeting_health != null ? stats.avg_meeting_health : "—"}
                  {stats.avg_meeting_health != null && <span className="text-xl text-muted-foreground">/100</span>}
                </div>
                <div className="text-sm text-muted-foreground mt-2">
                  {stats.avg_on_agenda != null ? `${stats.avg_on_agenda}% on-agenda` : "No analysed meetings yet"}
                  {stats.meetings_analyzed ? ` · ${stats.meetings_analyzed} meetings` : ""}
                </div>
              </div>
              <div className="mt-4 grid grid-cols-2 gap-2 text-center">
                <div className="rounded-xl bg-secondary/60 py-2">
                  <div className="font-heading text-2xl">{stats.closure_rate}%</div>
                  <div className="eyebrow mt-0.5">Closure rate</div>
                </div>
                <div className="rounded-xl bg-secondary/60 py-2">
                  <div className="font-heading text-2xl">{stats.decisions_pending}</div>
                  <div className="eyebrow mt-0.5">Decisions pending</div>
                </div>
              </div>
            </div>

            {/* Task status donut */}
            <div data-testid="analytics-status-donut" className="yl-surface p-6">
              <div className="eyebrow mb-1">Tasks overview</div>
              {(() => {
                const palette = { Open: "hsl(var(--muted-foreground))", "In Progress": "hsl(var(--chart-2))", "In Review": "hsl(var(--chart-3))", Done: "hsl(var(--ink-success))", Blocked: "hsl(var(--destructive))", Cancelled: "hsl(var(--border))" };
                const data = Object.entries(stats.by_status || {}).filter(([, v]) => v > 0).map(([name, value]) => ({ name, value }));
                const total = (data || []).reduce((a, d) => a + d.value, 0);
                const cells = data.length ? data : [{ name: "None", value: 1 }];
                return (
                  <div className="flex items-center gap-3">
                    <div style={{ width: 110, height: 110, position: "relative" }}>
                      <ResponsiveContainer>
                        <PieChart>
                          <Pie data={cells} dataKey="value" innerRadius={34} outerRadius={52} paddingAngle={2} stroke="none">
                            {(cells || []).map((d) => (
                              <Cell key={d.name} fill={palette[d.name] || "hsl(var(--border))"} />
                            ))}
                          </Pie>
                        </PieChart>
                      </ResponsiveContainer>
                      <div style={{ position: "absolute", inset: 0 }} className="flex flex-col items-center justify-center">
                        <div className="font-heading text-2xl leading-none">{total}</div>
                        <div className="text-[9px] uppercase tracking-wider text-muted-foreground">tasks</div>
                      </div>
                    </div>
                    <div className="flex-1 space-y-1">
                      {(data || []).slice(0, 5).map((d) => (
                        <div key={d.name} className="flex items-center justify-between text-xs">
                          <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full" style={{ background: palette[d.name] || "hsl(var(--border))" }} />{d.name}</span>
                          <span className="font-semibold">{d.value}</span>
                        </div>
                      ))}
                      {data.length === 0 && <div className="text-xs text-muted-foreground">No tasks yet.</div>}
                    </div>
                  </div>
                );
              })()}
            </div>

            {/* Ageing buckets chart */}
            <div data-testid="analytics-ageing" className="yl-surface p-6">
              <div className="eyebrow mb-3">Open task ageing</div>
              <div style={{ width: "100%", height: 170 }}>
                <ResponsiveContainer>
                  <BarChart data={Object.entries(stats.ageing_buckets || {}).map(([name, count]) => ({ name, count }))}>
                    <XAxis dataKey="name" tick={{ fontSize: 10 }} axisLine={false} tickLine={false} />
                    <Bar dataKey="count" radius={[6, 6, 0, 0]}>
                      {(Object.keys(stats.ageing_buckets || {})).map((b, i) => (
                        <Cell key={b} fill={["hsl(var(--chart-1))", "hsl(var(--chart-3))", "hsl(var(--chart-4))", "hsl(var(--chart-5))"][i % 4]} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Workload */}
            <div data-testid="analytics-workload" className="yl-surface p-6">
              <div className="eyebrow mb-3">Workload (open)</div>
              <div className="space-y-2">
                {(stats.workload || []).slice(0, 6).map((w) => (
                  <div key={w.name} className="flex items-center gap-2">
                    <div className="w-20 truncate text-xs">{w.name}</div>
                    <div className="flex-1 h-2 rounded-full bg-secondary overflow-hidden">
                      <div className="h-full bg-primary rounded-full" style={{ width: `${Math.min(100, (w.count / ((stats.workload[0] || {}).count || 1)) * 100)}%` }} />
                    </div>
                    <div className="w-6 text-right text-xs font-semibold">{w.count}</div>
                  </div>
                ))}
                {(stats.workload || []).length === 0 && <div className="text-sm text-muted-foreground">No open tasks.</div>}
              </div>
            </div>
          </div>

          {/* Needs attention */}
          <div data-testid="analytics-needs-attention" className="yl-surface overflow-hidden">
            <div className="px-5 py-3 border-b border-border flex items-center gap-2 tone-blush">
              <AlertTriangle className="w-3.5 h-3.5" strokeWidth={2} />
              <div className="eyebrow">Needs attention</div>
            </div>
            <ul className="divide-y divide-border">
              {(stats.needs_attention || []).slice(0, 6).map((t) => (
                <li key={t.id} data-testid={`attention-${t.id}`} className="p-4 flex items-center justify-between gap-4 hover:bg-secondary/40">
                  <div className="min-w-0">
                    <div className="font-medium text-sm truncate">{t.title}</div>
                    <div className="text-xs text-muted-foreground mt-0.5">{t.assigned_to_name || "Unassigned"}{t.due_date ? ` · due ${t.due_date}` : ""}</div>
                  </div>
                  <Link to="/tasks" data-testid={`attention-open-${t.id}`} className="text-xs font-semibold underline underline-offset-4 shrink-0">Open</Link>
                </li>
              ))}
              {(stats.needs_attention || []).length === 0 && (
                <li className="p-6 text-sm text-muted-foreground">Nothing needs attention right now.</li>
              )}
            </ul>
          </div>
        </section>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <section className="yl-surface overflow-hidden">
          <div className="px-5 py-3 border-b border-border flex items-center justify-between tone-lavender">
            <div className="eyebrow">
              {role === "Participant" ? "My upcoming meetings" : "Upcoming meetings"}
            </div>
            <Link to="/meetings" data-testid="dash-all-meetings-link" className="text-[10px] uppercase tracking-[0.2em] font-semibold underline-offset-4 hover:underline">
              All
            </Link>
          </div>
          <ul className="divide-y divide-border">
            {(data?.upcoming_meetings || []).length === 0 && (
              <li className="p-6 text-sm text-muted-foreground">No upcoming meetings scheduled.</li>
            )}
            {(data?.upcoming_meetings || []).map((m) => (
              <li key={m.id} className="p-5 flex items-start justify-between gap-4 hover:bg-secondary/40">
                <div className="min-w-0">
                  <div className="font-semibold truncate">{m.title}</div>
                  <div className="text-xs text-muted-foreground mt-1">
                    {m.meeting_date} · {m.start_time}
                    {m.end_time ? ` – ${m.end_time}` : ""}
                  </div>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <StatusBadge value={m.status} />
                  <Link
                    data-testid={`dash-meeting-link-${m.id}`}
                    to={`/meetings/${m.id}`}
                    className="text-xs font-semibold underline underline-offset-4"
                  >
                    Open
                  </Link>
                </div>
              </li>
            ))}
          </ul>
        </section>

        <section className="yl-surface overflow-hidden">
          <div className="px-5 py-3 border-b border-border flex items-center justify-between tone-sage">
            <div className="eyebrow">
              {role === "Reviewer" || role === "Admin" ? "Recent decisions" : "Recent completed"}
            </div>
            <Link to={role === "Reviewer" || role === "Admin" ? "/decisions" : "/meetings"} data-testid="dash-all-recent-link" className="text-[10px] uppercase tracking-[0.2em] font-semibold underline-offset-4 hover:underline">
              All
            </Link>
          </div>
          {role === "Reviewer" || role === "Admin" ? (
            <ul className="divide-y divide-border" data-testid="dashboard-recent-decisions">
              {(data?.recent_decisions || []).length === 0 && (
                <li className="p-6 text-sm text-muted-foreground">No decisions captured yet.</li>
              )}
              {(data?.recent_decisions || []).map((d) => (
                <li key={d.id} className="p-5 hover:bg-secondary/40">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="font-semibold text-sm leading-snug">{d.decision_text}</div>
                      <div className="text-xs text-muted-foreground mt-1.5 flex flex-wrap gap-x-3">
                        <span>Owner: {d.decision_owner_name || "Unassigned"}</span>
                        <span>·</span>
                        <span>Priority: {d.priority || "Medium"}</span>
                      </div>
                    </div>
                    <Sparkles className="w-3.5 h-3.5 text-primary mt-1 shrink-0" strokeWidth={2} />
                  </div>
                  <Link
                    data-testid={`dash-decision-meeting-${d.meeting_id}`}
                    to={`/meetings/${d.meeting_id}`}
                    className="mt-3 inline-block text-xs font-semibold underline underline-offset-4"
                  >
                    View meeting →
                  </Link>
                </li>
              ))}
            </ul>
          ) : (
            <ul className="divide-y divide-border">
              {(data?.recent_meetings || []).length === 0 && (
                <li className="p-6 text-sm text-muted-foreground">No completed meetings yet.</li>
              )}
              {(data?.recent_meetings || []).map((m) => (
                <li key={m.id} className="p-5 hover:bg-secondary/40">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="font-semibold truncate">{m.title}</div>
                      <div className="text-xs text-muted-foreground mt-1">{m.meeting_date}</div>
                      {m.summary && (
                        <p className="text-sm mt-2 line-clamp-2 text-muted-foreground">{m.summary}</p>
                      )}
                    </div>
                    <StatusBadge value={m.status} />
                  </div>
                  <Link
                    data-testid={`dash-recent-meeting-${m.id}`}
                    to={`/meetings/${m.id}`}
                    className="mt-3 inline-block text-xs font-semibold underline underline-offset-4"
                  >
                    View intelligence →
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>

      {/* Participant — show personal task list */}
      {role === "Participant" && (
        <section className="yl-surface overflow-hidden" data-testid="participant-tasks">
          <div className="px-5 py-3 border-b border-border flex items-center justify-between tone-apricot">
            <div className="eyebrow">My open tasks</div>
            <Link to="/tasks" data-testid="dash-all-tasks-link" className="text-[10px] uppercase tracking-[0.2em] font-semibold underline-offset-4 hover:underline">
              All
            </Link>
          </div>
          <ul className="divide-y divide-border">
            {(data?.my_tasks || []).length === 0 && (
              <li className="p-6 text-sm text-muted-foreground">No tasks assigned to you yet.</li>
            )}
            {(data?.my_tasks || []).map((t) => (
              <li key={t.id} className="p-5 flex items-start justify-between gap-4 hover:bg-secondary/40">
                <div className="min-w-0 flex items-start gap-3">
                  <ClipboardCheck className="w-4 h-4 text-primary mt-0.5 shrink-0" strokeWidth={2} />
                  <div className="min-w-0">
                    <div className="font-semibold truncate">{t.title}</div>
                    <div className="text-xs text-muted-foreground mt-1">
                      {t.due_date ? `Due ${t.due_date}` : "No due date"} · {t.priority || "Medium"}
                    </div>
                  </div>
                </div>
                <StatusBadge value={t.status} />
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}
